import { useContext, useEffect, useState } from 'react';

import DealerReportLoader from './components/DealerReportLoader';
import {
  CardWrapStyled,
  CardsWrapStyled,
  ClaimLabelStyled,
  ClaimReportInnerContentStyled,
  DealerReportWrapStyled,
  InnerContainerStyled,
  InnerContentRowStyled,
  InnerFlexContainerStyled,
  InvoicePercentageStyled,
  StatisticSignLargeStyled,
  StatisticSignStyled,
} from './DealerReport.styled';
import useDealerReport from './hooks/useDealerReport';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import Typography from 'components/Typography/Typography';
import { TicketsContext } from 'contexts/TicketsContext';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';

type DealerReportProps = {
  isReportOpened: boolean;
};

const DealerReport: React.FC<DealerReportProps> = ({ isReportOpened }) => {
  /**
   * Hooks
   */
  const { isLoadingReport, claimReport } = useDealerReport();

  const { getSearchParam } = useContext(TicketsContext);

  const [clickedCardIndex, setClickedCardIndex] = useState(-1);
  const isPositive = (value: number) => value > 0;

  useEffect(() => {
    if (clickedCardIndex !== -1 && getSearchParam(TicketsRefinementKeysE.PastMonth) === null)
      setClickedCardIndex(-1);
  }, [getSearchParam, clickedCardIndex]);

  return (
    <DealerReportWrapStyled className="dealer-report">
      <CardsWrapStyled justify="start" align="stretch" isReportOpened={isReportOpened}>
        {isLoadingReport ? (
          <DealerReportLoader />
        ) : (
          <>
            {claimReport?.map(
              (
                {
                  totalClaimCount,
                  totalClaimAmount,
                  averageClaimAmount,
                  invoiceDateStr,
                  invoiceStartDateStr,
                  averageClaimPercentage,
                  totalRevenuePercentage,
                  totalClaimPercentage,
                },
                index
              ) => (
                <CardWrapStyled
                  isActive={false}
                  direction="column"
                  justify="start"
                  key={`dealer-${index}`}
                >
                  <ClaimReportInnerContentStyled align="start" direction="column">
                    <InnerContentRowStyled>
                      <InnerContainerStyled>
                        <Typography variant="body4" color="darkGrey">
                          {invoiceDateStr}
                        </Typography>
                        <Typography variant="h2" weight="medium">
                          $&nbsp;
                          {Math.abs(totalClaimAmount).toLocaleString('en-US', {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })}
                        </Typography>
                        <div>
                          <ClaimLabelStyled variant="body4" color="darkGrey">
                            Claims
                          </ClaimLabelStyled>
                          <FlexContainer justify="start">
                            <Typography color="black" size="xl">
                              {totalClaimCount}
                            </Typography>
                            {totalClaimPercentage ? (
                              <FlexContainer>
                                <StatisticSignStyled
                                  isPositive={isPositive(totalClaimPercentage)}
                                />
                                <InvoicePercentageStyled
                                  isPositive={isPositive(totalClaimPercentage)}
                                >
                                  {totalClaimPercentage.toFixed(2)}%
                                </InvoicePercentageStyled>
                              </FlexContainer>
                            ) : null}
                          </FlexContainer>
                        </div>
                      </InnerContainerStyled>
                      <InnerFlexContainerStyled>
                        {totalRevenuePercentage ? (
                          <>
                            <Typography
                              variant="body4"
                              color="darkGrey"
                              style={{
                                display: isPositive(totalRevenuePercentage) ? 'block' : 'none',
                              }}
                            >
                              From {invoiceStartDateStr}
                            </Typography>
                            <FlexContainer justify="start">
                              <StatisticSignLargeStyled
                                isPositive={isPositive(totalRevenuePercentage)}
                              />
                              <InvoicePercentageStyled
                                variant="h2"
                                weight="medium"
                                isPositive={isPositive(totalRevenuePercentage)}
                              >
                                {totalRevenuePercentage.toFixed(2)}%
                              </InvoicePercentageStyled>
                            </FlexContainer>
                          </>
                        ) : null}
                        <InnerContainerStyled>
                          <div>
                            <ClaimLabelStyled variant="body4" color="darkGrey">
                              Avg Claim
                            </ClaimLabelStyled>
                            <FlexContainer justify="start">
                              <Typography color="black" size="xl">
                                ${averageClaimAmount.toFixed(2)}
                              </Typography>
                              {averageClaimPercentage ? (
                                <FlexContainer>
                                  <StatisticSignStyled
                                    isPositive={isPositive(averageClaimPercentage)}
                                  />
                                  <InvoicePercentageStyled
                                    variant="body4"
                                    isPositive={isPositive(averageClaimPercentage)}
                                  >
                                    {averageClaimPercentage.toFixed(2)}%
                                  </InvoicePercentageStyled>
                                </FlexContainer>
                              ) : null}
                            </FlexContainer>
                          </div>
                        </InnerContainerStyled>
                      </InnerFlexContainerStyled>
                    </InnerContentRowStyled>
                  </ClaimReportInnerContentStyled>
                </CardWrapStyled>
              )
            )}
          </>
        )}
      </CardsWrapStyled>
    </DealerReportWrapStyled>
  );
};

export default DealerReport;
